import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Attachment {
  id: string;
  name: string;
  size?: number;
  type?: string;
}

export interface Email {
  id: string;
  from: string;
  to?: string;
  subject: string;
  body?: string;
  date?: string;
  timestamp?: Date;
  read: boolean;
  starred: boolean;
  attachments?: Attachment[];
}

interface EmailStore {
  emails: Email[];
  selectedEmailId: string | null;
  searchQuery: string;

  // Actions
  addEmail: (email: Email) => void;
  removeEmail: (id: string) => void;
  updateEmail: (id: string, updates: Partial<Email>) => void;
  selectEmail: (id: string | null) => void;
  setSearchQuery: (query: string) => void;
  toggleRead: (id: string) => void;
  toggleStarred: (id: string) => void;
  getSelectedEmail: () => Email | undefined;
  searchEmails: () => Email[];
  resetToInitialState: () => void;
}

const initialEmails: Email[] = [
  {
    id: '3',
    from: 'dev@laura.ai',
    subject: 'On-Call Shift Notice: Your Rotation Has Started',
    date: 'Today 03:00 AM',
    body: 'Developer On-Call Notice\n\nThis is an automated notification that your on-call shift has begun.',
    read: false,
    starred: false,
    attachments: [
      {
        id: 'attach-1',
        name: 'oncall-notice.txt',
        size: 2048,
        type: 'text/plain',
      },
    ],
  },
  {
    id: '1',
    from: 'hr@laura.ai',
    subject: 'ACTION REQUIRED: NDA Signature - Immediate Compliance Needed',
    date: 'Today 12:01 AM',
    read: false,
    starred: false,
  },
  {
    id: '2',
    from: 'announcement@laura.ai',
    subject:
      'Company-Wide Announcement: Enhanced Security and Privacy Measures',
    date: 'Today 12:00 AM',
    body: 'All Team Members,\n\nDue to the incident, enhanced security and privacy measures are now enforced across all Laura.ai systems effective immediately.\n\nKey Changes:\n\n• All emails dated prior to Today (01/30/2030) have been deleted from the system\n• All chat messages across all channels have been wiped\n• File permissions have been updated with stricter access controls\n• Badge authentication is now mandatory for all oncall\n• Data retention policies have been revised and enforced\n\nWhat This Means for You:\n- Review and update your file access permissions\n- Re-authenticate all connected applications\n- Contact IT if you require access to specific archived materials\n\nWe appreciate your understanding as we prioritize the security and privacy of our systems and data.\n\nFor questions or concerns, please reach out to security@laura.ai.\n\nThank you,\nLaura.ai Leadership Team',
    read: false,
    starred: false,
  },
  {
    id: '0',
    from: 'notification@laura.ai',
    subject: 'System Notice: Email Data Deletion Completed',
    date: '4 days ago',
    body: 'System Notification\n\nAll email data has been purged from the system.\nNormal email operations have resumed. No action required.\n\n\nThis is an automated message. Please do not reply.',
    read: true,
    starred: false,
  },
];

export const useEmailStore = create<EmailStore>()(
  persist(
    (set, get) => ({
      emails: initialEmails,
      selectedEmailId: initialEmails[3]?.id ?? null,
      searchQuery: '',

      addEmail: (email) =>
        set((state) => ({
          emails: [email, ...state.emails],
        })),

      removeEmail: (id) =>
        set((state) => ({
          emails: state.emails.filter((email) => email.id !== id),
          selectedEmailId:
            state.selectedEmailId === id ? null : state.selectedEmailId,
        })),

      updateEmail: (id, updates) =>
        set((state) => ({
          emails: state.emails.map((email) =>
            email.id === id ? { ...email, ...updates } : email,
          ),
        })),

      selectEmail: (id) =>
        set((state) => {
          // Mark email as read when selected
          const updatedEmails =
            id !== null
              ? state.emails.map((email) =>
                  email.id === id ? { ...email, read: true } : email,
                )
              : state.emails;

          return {
            selectedEmailId: id,
            emails: updatedEmails,
          };
        }),

      setSearchQuery: (query) =>
        set({
          searchQuery: query,
        }),

      toggleRead: (id) =>
        set((state) => ({
          emails: state.emails.map((email) =>
            email.id === id ? { ...email, read: !email.read } : email,
          ),
        })),

      toggleStarred: (id) =>
        set((state) => ({
          emails: state.emails.map((email) =>
            email.id === id ? { ...email, starred: !email.starred } : email,
          ),
        })),

      getSelectedEmail: () => {
        const state = get();
        return state.emails.find((email) => email.id === state.selectedEmailId);
      },

      searchEmails: () => {
        const state = get();
        const query = state.searchQuery.toLowerCase();
        if (!query) return state.emails;
        return state.emails.filter(
          (email) =>
            email.subject.toLowerCase().includes(query) ||
            email.from.toLowerCase().includes(query) ||
            email.body?.toLowerCase().includes(query),
        );
      },

      resetToInitialState: () =>
        set({
          emails: initialEmails,
          selectedEmailId: initialEmails[3]?.id ?? null,
          searchQuery: '',
        }),
    }),
    {
      name: 'email-store',
    },
  ),
);
